import React, {SyntheticEvent, useCallback, useEffect, useRef, useState} from "react"
import {useLocation} from "react-router"
import {Dropdown, Menu, Icon, Button, Portal} from "semantic-ui-react"
import {FormattedMessage} from "react-intl"
import {EventHandlers} from "./top-bar"


type CreatorMenuProps = {
    howItWorksOpen: boolean
    unsavedChanges: boolean
    loading: boolean
    allowConvert: boolean
    eventHandlers: EventHandlers
}

let helpClicked = false

export function CreatorMenu({ howItWorksOpen, unsavedChanges, loading, allowConvert, eventHandlers } : CreatorMenuProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [anchorRect, setAnchorRect] = useState<DOMRect>()
    const [unsavedLeaveWidth, setUnsavedLeaveWidth] = useState(0)
    const unsavedLeaveRef = useRef<HTMLDivElement>(null)
    const location = useLocation()
    const from = location.state?.from ?? null

    const handleUpload = useCallback((event: SyntheticEvent<HTMLInputElement>) => {
        const files = (event.target as HTMLInputElement).files
        if (!files || !files.length) return
        const file = files[0];
        (event.target as HTMLInputElement).value = ""
        eventHandlers.onImportXlsx!(file, "import_xlsx")
    }, [eventHandlers])

    /**
     * Unsaved changes dialog dimensions.
     */
    useEffect(() => { if (unsavedLeaveRef.current) setUnsavedLeaveWidth(unsavedLeaveRef.current.offsetWidth) }, [showConfirm])

    /**
     * Shutdown the help blinking.
     */
    const [, forceRender] = useState({})
    useEffect(() => {
        if (howItWorksOpen) {
            helpClicked = true
            forceRender({})
        }
    }, [howItWorksOpen])

    return (
        <>
            <Menu className="creator-menu linkblue" style={{marginLeft: "11em"}}>

                {/* HOME */}
                <Menu.Item key="home" onClick={(e) => {
                    if (unsavedChanges && allowConvert) {
                        setAnchorRect((e.currentTarget as HTMLElement).getBoundingClientRect())
                        setShowConfirm(true)
                    } else eventHandlers.onHome!()
                }}><Icon name="home"/><FormattedMessage id="menu.home"/></Menu.Item>

                {/* HOW TO */}
                <Menu.Item key="howitworks" onClick={() => eventHandlers.setHowItWorksOpen!(true)}>
                    <Icon name="question circle" className={!helpClicked ? "blinking" : ""}/>
                    <FormattedMessage id="menu.howitworks"/>
                </Menu.Item>

                {/* DOWNLOAD */}
                <Dropdown
                    className="item"
                    direction="left"
                    disabled={loading}
                    open={dropdownOpen}
                    onOpen={() => setDropdownOpen(true)}
                    onClose={() => setDropdownOpen(false)}
                    trigger={<div><Icon
                        name={unsavedChanges && allowConvert ? "save" : "laptop"}
                        className={unsavedChanges && allowConvert && !loading ? "unsaved-changes" : ""}
                    /><FormattedMessage id="menu.save.load"/></div>}>

                    <Dropdown.Menu>
                        {/* UPLOAD EXCEL */}
                        <Dropdown.Item as="label" htmlFor="xlsxInput" key="upload_xlsx">
                            <Icon name="upload"/><FormattedMessage id="menu.load.disk"/>
                        </Dropdown.Item>
                        <input id="xlsxInput" hidden type="file" accept=".xlsx,.xls" onChange={(e) => {
                            handleUpload(e)
                            setDropdownOpen(false)
                        }}/>

                        {/* DOWNLOAD EXCEL */}
                        <Dropdown.Item key="download_xlsx" disabled={!allowConvert} onClick={eventHandlers.onExportXlsx}>
                            <Icon name="save"/><FormattedMessage id="menu.save.disk"/>
                        </Dropdown.Item>

                    </Dropdown.Menu>
                </Dropdown>

                {/* CONVERT */}
                <Menu.Item
                    key="convert"
                    disabled={!allowConvert || loading}
                    // No need to blink if it is not the first edition
                    className={`convert-button ${allowConvert && !from ? "blink" : ""}`}
                    onClick={eventHandlers.onConvert}>
                    {loading
                        ? <><Icon id="loading-icon" name="spinner" loading/><span><FormattedMessage id="menu.loading"/></span></>
                        : <><Icon name="play circle"/><FormattedMessage id="menu.convert"/></>
                    }
                </Menu.Item>
            </Menu>

            {/* UNSAVED CHANGES CONFIRM */}
            <Portal open={showConfirm} onClose={() => setShowConfirm(false)}>
                {anchorRect ? (
                    <div ref={unsavedLeaveRef} className="unsaved-leave" style={{
                        top: anchorRect.bottom,
                        left: anchorRect.left + anchorRect.width / 2 - unsavedLeaveWidth / 2
                    }}>
                        {/* Header */}
                        <div className="header">
                            <h3><Icon name="home"/><FormattedMessage id="menu.unsaved.changes"/></h3>
                        </div>
                        {/* Content */}
                        <div className="content">
                            <p><FormattedMessage id="menu.use.save" values={{
                                save: <><span style={{ color: "coral", fontSize: 16, marginRight: 0 }}>
                                    <Icon name="save"/>
                                </span></>
                            }}/></p>
                            <p><FormattedMessage id="menu.without.saving"/></p>
                        </div>
                        {/* Actions */}
                        <div style={{ marginTop: 15 }}>
                            <Button onClick={() => setShowConfirm(false)}>
                                <FormattedMessage id="button.cancel"/>
                            </Button>
                            <Button color="red" onClick={() => {
                                setShowConfirm(false)
                                eventHandlers.onHome!()
                            }}><FormattedMessage id="button.leave.anyway"/></Button>
                        </div>
                    </div>
                ) : <></>}
            </Portal>
        </>
    )
}